import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import ApiService from '../services/SupabaseApiService';
import { supabase } from '../services/supabase';
import usePresence from '../hooks/usePresence';
import taskScheduler from '../services/TaskScheduler';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

// Notification Service
class NotificationService {
  static async requestPermission() {
    if (!('Notification' in window)) {
      return false;
    }
    
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  static async sendNotification(title, body, icon = '/logo192.png') {
    if (!('Notification' in window)) {
      return;
    }
    
    if (Notification.permission === 'granted') {
      new Notification(title, { body, icon });
    }
  }
  
  static async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        return registration;
      } catch (error) {
        console.error('Service worker registration failed:', error);
        return null;
      }
    }
    return null;
  }

  static async subscribeToPush(registration, vapidPublicKey) {
    try {
      // Wait until the SW is active (subscribe fails if still installing)
      if (registration.installing) {
        await new Promise((resolve) => {
          registration.installing.addEventListener('statechange', function onState() {
            if (this.state === 'activated') {
              this.removeEventListener('statechange', onState)
              resolve()
            }
          })
        })
      } else if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      }
      await navigator.serviceWorker.ready

      // Replace stale subscriptions tied to an old/invalid VAPID key
      const existing = await registration.pushManager.getSubscription()
      if (existing) {
        try { await existing.unsubscribe() } catch (_) { /* ignore */ }
      }

      const applicationServerKey = NotificationService.urlBase64ToUint8Array(vapidPublicKey)
      if (!applicationServerKey || applicationServerKey.byteLength < 65) {
        throw new Error('Invalid VAPID public key length')
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })

      return subscription
    } catch (error) {
      console.error('Push subscription failed:', error)
      throw error
    }
  }

  static async unsubscribeFromPush(registration) {
    try {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Push unsubscription failed:', error);
      return false;
    }
  }

  static urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  static isSupported() {
    return typeof window !== 'undefined' && 
           'serviceWorker' in navigator && 
           'Notification' in window && 
           'PushManager' in window;
  }

  static async sendLocalNotification(registration, title, options = {}) {
    if (!registration) return false;
    
    const defaultOptions = {
      body: 'New activity in TrophyTime!',
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'trophytime-local',
      requireInteraction: false,
      actions: [
        {
          action: 'view',
          title: 'View App'
        }
      ],
      data: {
        url: '/',
        timestamp: Date.now()
      }
    };

    try {
      await registration.showNotification(title, { ...defaultOptions, ...options });
      return true;
    } catch (error) {
      console.error('Local notification failed:', error);
      return false;
    }
  }
}

export const DataProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [family, setFamily] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [initializationComplete, setInitializationComplete] = useState(false);

  // App settings including debug mode
  const [appSettings, setAppSettings] = useState(() => {
    const saved = localStorage.getItem('trophytime_app_settings');
    return saved ? JSON.parse(saved) : {
      showDebugInfo: false,
      theme: 'auto',
      notifications: true,
      sounds: true
    };
  });

  // Push notification state
  const [pushNotificationState, setPushNotificationState] = useState({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    subscription: null,
    serviceWorkerRegistration: null
  });

  // VAPID public key - You'll need to generate this
  const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'YOUR_VAPID_PUBLIC_KEY_HERE';

  // Presence tracking for family calling
  const {
    onlineMembers,
    isOnline: isUserOnline,
    isAvailableForCalls,
    toggleAvailability,
    refreshPresence
  } = usePresence(
    user?.role || 'parent', // Assuming parents are main users, kids when they have their own accounts
    user?.id,
    user?.familyId || family?.id,
    user?.parentName || '' // display name broadcast with presence so others see WHO is online
  );

  // Save app settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('trophytime_app_settings', JSON.stringify(appSettings));
  }, [appSettings]);

  const updateAppSettings = (updates) => {
    setAppSettings(prev => ({ ...prev, ...updates }));
  };

  // Initialize push notifications
  useEffect(() => {
    const initializePushNotifications = async () => {
      if (!NotificationService.isSupported()) {
        console.log('Push notifications not supported');
        return;
      }

      setPushNotificationState(prev => ({ ...prev, isSupported: true }));

      try {
        // Register service worker
        const registration = await NotificationService.registerServiceWorker();
        if (registration) {
          setPushNotificationState(prev => ({ 
            ...prev, 
            serviceWorkerRegistration: registration 
          }));

          // Check current subscription
          const existingSubscription = await registration.pushManager.getSubscription();
          if (existingSubscription) {
            setPushNotificationState(prev => ({
              ...prev,
              isSubscribed: true,
              subscription: existingSubscription
            }));
          }
        }

        // Update permission state
        setPushNotificationState(prev => ({
          ...prev,
          permission: Notification.permission
        }));

      } catch (error) {
        console.error('Push notification initialization failed:', error);
      }
    };

    initializePushNotifications();
  }, []);

  // Request notification permission and subscribe to push
  const requestNotificationPermission = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) {
        toast.error('Notifications are not supported in this browser');
        return false;
      }

      const permissionGranted = await NotificationService.requestPermission();
      setPushNotificationState(prev => ({ ...prev, permission: Notification.permission }));

      if (!permissionGranted) {
        toast.error(
          Notification.permission === 'denied'
            ? 'Notifications are blocked. Enable them in browser settings.'
            : 'Notification permission was not granted'
        );
        return false;
      }

      const vapidKey = (VAPID_PUBLIC_KEY || '').trim().replace(/^['"]|['"]$/g, '');
      const isPlaceholder =
        !vapidKey ||
        /your_vapid|YOUR_VAPID|placeholder|changeme/i.test(vapidKey) ||
        vapidKey.length < 80;

      if (isPlaceholder) {
        toast.error('Push is not configured — set a real REACT_APP_VAPID_PUBLIC_KEY in .env.local, then rebuild/deploy.');
        return false;
      }

      // Don't trust stale React state — always resolve the active SW registration
      let registration = pushNotificationState.serviceWorkerRegistration;
      if (!registration && 'serviceWorker' in navigator) {
        registration = await navigator.serviceWorker.ready;
        setPushNotificationState(prev => ({
          ...prev,
          serviceWorkerRegistration: registration
        }));
      }

      if (!registration) {
        toast.error('Service worker not ready yet. Wait a moment and try again.');
        return false;
      }

      let subscription
      try {
        subscription = await NotificationService.subscribeToPush(registration, vapidKey)
      } catch (subscribeError) {
        console.error('Push subscribe error:', subscribeError)
        toast.error(
          subscribeError?.message?.includes('VAPID') || subscribeError?.message?.includes('applicationServerKey')
            ? 'Invalid VAPID key. Set a real REACT_APP_VAPID_PUBLIC_KEY and rebuild.'
            : `Push subscribe failed: ${subscribeError.message || 'reload and try again'}`
        )
        return false
      }

      if (!subscription) {
        toast.error('Could not create push subscription. Try reloading the page.')
        return false
      }

      setPushNotificationState(prev => ({
        ...prev,
        isSubscribed: true,
        subscription
      }))

      await saveSubscriptionToBackend(subscription)
      return true
    } catch (error) {
      console.error('Failed to request notification permission:', error)
      toast.error(error.message || 'Failed to enable notifications')
      return false
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    try {
      if (pushNotificationState.serviceWorkerRegistration) {
        const success = await NotificationService.unsubscribeFromPush(
          pushNotificationState.serviceWorkerRegistration
        );
        
        if (success) {
          setPushNotificationState(prev => ({
            ...prev,
            isSubscribed: false,
            subscription: null
          }));

          // Remove subscription from backend
          await removeSubscriptionFromBackend();
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Failed to unsubscribe from push:', error);
      return false;
    }
  };

  // Send local notification
  const sendLocalNotification = async (title, options = {}) => {
    if (pushNotificationState.serviceWorkerRegistration) {
      return await NotificationService.sendLocalNotification(
        pushNotificationState.serviceWorkerRegistration,
        title,
        options
      );
    }
    return false;
  };

  // Save subscription to backend (Supabase)
  const saveSubscriptionToBackend = async (subscription) => {
    try {
      if (!user || !family) return;

      const subscriptionData = {
        user_id: user.id,
        family_id: family.id,
        endpoint: subscription.endpoint,
        p256dh_key: subscription.keys.p256dh,
        auth_key: subscription.keys.auth,
        created_at: new Date().toISOString(),
        is_active: true
      };

      const { error } = await ApiService.supabase
        .from('push_subscriptions')
        .upsert(subscriptionData, { 
          onConflict: 'user_id,endpoint',
          ignoreDuplicates: false 
        });

      if (error) {
        console.error('Failed to save subscription to backend:', error);
      }
    } catch (error) {
      console.error('Error saving subscription:', error);
    }
  };

  // Remove subscription from backend
  const removeSubscriptionFromBackend = async () => {
    try {
      if (!user) return;

      const { error } = await ApiService.supabase
        .from('push_subscriptions')
        .update({ is_active: false })
        .eq('user_id', user.id);

      if (error) {
        console.error('Failed to remove subscription from backend:', error);
      }
    } catch (error) {
      console.error('Error removing subscription:', error);
    }
  };

  // Initialize app when context starts
  useEffect(() => {
    const initializeApp = async () => {
      console.log('🚀 Initializing TrophyTime App...');
      
      try {
        // Check for email confirmation callback (Supabase adds hash fragments)
        const handleEmailConfirmation = async () => {
          try {
            // Check URL for Supabase auth callback
            const hash = window.location.hash;
            if (!hash || hash.length === 0) {
              return; // No hash, skip
            }
            
            const hashParams = new URLSearchParams(hash.substring(1));
            const accessToken = hashParams.get('access_token');
            const type = hashParams.get('type');
            
            console.log('🔍 Checking email confirmation callback:', { type, hasToken: !!accessToken });
            
            // Check for any signup confirmation (not just type='signup', also check for confirmed email)
            if (accessToken) {
              console.log('📧 Auth token detected, checking for email confirmation...');
              
              // First, set the session from the hash (Supabase needs this)
              const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: hashParams.get('refresh_token') || ''
              });
              
              if (sessionError) {
                console.error('❌ Error setting session:', sessionError);
              }
              
              // Get the current user session
              const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
              
              if (userError) {
                console.error('❌ Error getting user after confirmation:', userError);
                return;
              }
              
              if (currentUser && currentUser.email_confirmed_at) {
                console.log('✅ Email confirmed, checking for family / invite record...', {
                  userId: currentUser.id,
                  email: currentUser.email,
                  metadata: currentUser.user_metadata
                });

                // Invited members: link pending invitation after email confirmation
                const invitationToken = currentUser.user_metadata?.invitation_token;
                if (invitationToken) {
                  console.log('🔗 Invitation token in metadata — accepting invite after confirm');
                  try {
                    const FamilyMemberService = (await import('../services/FamilyMemberService')).default;
                    const inviteResult = await FamilyMemberService.acceptInvitationWithAuth(invitationToken);
                    if (inviteResult.success) {
                      console.log('✅ Invitation accepted after email confirmation', inviteResult);
                      toast.success('Welcome! You have joined the family.');
                      window.location.hash = '';
                      setTimeout(() => {
                        window.location.reload();
                      }, 1000);
                      return;
                    }
                    console.error('❌ Invite accept after confirm failed:', inviteResult.error);
                    toast.error(inviteResult.error || 'Could not join family after email confirmation.');
                  } catch (inviteError) {
                    console.error('❌ Invite accept after confirm error:', inviteError);
                    toast.error('Could not join family after email confirmation. Please open your invite link again.');
                  }
                }
                
                // Check if family record exists (family owners)
                try {
                  const familyData = await ApiService.getFamilyData();
                  if (familyData) {
                    console.log('✅ Family record already exists');
                    return; // Family exists, nothing to do
                  }
                } catch (familyError) {
                  // Family doesn't exist - complete setup for family owners only
                  console.log('⚠️ Family record not found, completing setup...', familyError);
                  
                  // Get user metadata from signup
                  const parentName = currentUser.user_metadata?.parent_name || 'Parent';
                  const familyName = currentUser.user_metadata?.family_name || 'My Family';
                  
                  console.log('📝 Found signup metadata:', { parentName, familyName });
                  
                  if (parentName !== 'Parent' && familyName !== 'My Family') {
                    console.log('🔄 Completing family account setup with metadata:', { parentName, familyName });
                    const result = await ApiService.completeFamilyAccountSetup(
                      currentUser.id,
                      currentUser.email,
                      parentName,
                      familyName
                    );
                    
                    if (result.success || result.family) {
                      console.log('✅ Family account setup completed!', result);
                      toast.success('Welcome! Your account is now fully set up.');
                      
                      // Clear hash and reload to load family data
                      window.location.hash = '';
                      setTimeout(() => {
                        window.location.reload();
                      }, 1000);
                    } else {
                      console.error('❌ Family setup failed:', result);
                      toast.error('Family setup failed. Please try logging in.');
                    }
                  } else {
                    console.warn('⚠️ Missing signup metadata, cannot auto-create family');
                  }
                }
              } else {
                console.log('⏳ Email not yet confirmed or user not found');
              }
            }
          } catch (error) {
            console.error('❌ Error handling email confirmation:', error);
            toast.error('Error completing setup. Please try logging in.');
          }
        };
        
        await handleEmailConfirmation();
        
        // Restore session on page refresh: Supabase keeps the auth session.
        // Prefer matching localStorage cache; otherwise rebuild user/family
        // from the live session (cache missing or email mismatch).
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const cachedUserRaw = localStorage.getItem('user');

          if (session?.user) {
            let restoredUser = null;
            let restoredFamily = null;

            if (cachedUserRaw) {
              try {
                const cachedUser = JSON.parse(cachedUserRaw);
                if (cachedUser?.email === session.user.email && cachedUser?.id === session.user.id) {
                  console.log('🔄 Restoring session from cache for:', cachedUser.email);
                  try {
                    restoredFamily = await ApiService.getFamilyData();
                    if (restoredFamily) {
                      restoredUser = cachedUser;
                    }
                  } catch (familyError) {
                    console.warn('⚠️ Cached restore getFamilyData failed, rebuilding:', familyError.message);
                    localStorage.removeItem('user');
                    localStorage.removeItem('familyData');
                  }
                } else {
                  console.log('🗑️ Cached user does not match session, rebuilding from auth...');
                  localStorage.removeItem('user');
                  localStorage.removeItem('familyData');
                }
              } catch (parseError) {
                console.warn('⚠️ Invalid cached user JSON, rebuilding from auth:', parseError.message);
                localStorage.removeItem('user');
                localStorage.removeItem('familyData');
              }
            }

            if (!restoredUser) {
              console.log('🔄 Rebuilding session from Supabase auth for:', session.user.email);
              const rebuilt = await ApiService.restoreSessionFromAuth(session.user);
              restoredUser = rebuilt.user;
              restoredFamily = rebuilt.family;
            }

            if (restoredUser && restoredFamily) {
              setUser(restoredUser);
              setFamily(restoredFamily);
              setIsAuthenticated(true);
              console.log('✅ Session restored successfully');
            }
          } else if (cachedUserRaw) {
            // Supabase session expired - clear stale cache
            console.log('🗑️ No active Supabase session, clearing stale cache...');
            localStorage.removeItem('user');
            localStorage.removeItem('familyData');
            localStorage.removeItem('authToken');
          }
        } catch (restoreError) {
          console.warn('⚠️ Session restore failed, falling back to login:', restoreError.message);
        }

        setIsLoading(false);
        console.log('✅ App initialization complete');
      } catch (error) {
        console.error('❌ App initialization failed:', error);
        setIsLoading(false); // Always stop loading on error
      }
    };

    const setupNetworkListeners = () => {
      const handleOnline = () => {
        console.log('🌐 Network: Online');
        setIsOnline(true);
        // Sync any pending data when back online
        console.log('Syncing offline data...');
      };

      const handleOffline = () => {
        console.log('📴 Network: Offline');
        setIsOnline(false);
      };

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    };

    const setupSubscriptionChangeListener = () => {
      const handleFamilyDataUpdate = (event) => {
        console.log('🔄 Received family data update event:', event.detail);
        const { family, reason } = event.detail;
        
        if (reason === 'subscription_change' || reason === 'manual_refresh') {
          console.log('📊 Updating family data due to:', reason);
          setFamily(family);
          
          // Show appropriate notification to the user
          if (reason === 'subscription_change') {
            console.log('✨ Subscription features updated!');
          } else if (reason === 'manual_refresh') {
            console.log('🔄 Family data refreshed manually');
          }
        }
      };

      window.addEventListener('familyDataUpdated', handleFamilyDataUpdate);

      return () => {
        window.removeEventListener('familyDataUpdated', handleFamilyDataUpdate);
      };
    };

    initializeApp();
    const cleanup = setupNetworkListeners();
    const subscriptionCleanup = setupSubscriptionChangeListener();
    
    return () => {
      cleanup();
      subscriptionCleanup();
      // Stop task scheduler on unmount
      taskScheduler.stop();
    };
  }, []);

  // Start the recurring-task scheduler once the user is actually logged in
  // (previously attempted in the mount effect, where auth state was always false)
  useEffect(() => {
    if (isAuthenticated && family) {
      console.log('📅 Starting task scheduler...');
      taskScheduler.start();
      return () => taskScheduler.stop();
    }
  }, [isAuthenticated, family]);

  // The browser can only hold ONE Supabase session. If a different account
  // signs in (e.g. in another tab), this tab's UI would keep showing the old
  // user while every request actually runs as the new one — so detect the
  // switch and reset this tab instead of silently mixing accounts.
  const activeUserRef = useRef(null);
  useEffect(() => {
    activeUserRef.current = user;
  }, [user]);

  // True while an intentional logout is in progress. During logout we do a full
  // page navigation to tear the app down cleanly — we must NOT flip React auth
  // state (which re-renders the app into a half-logged-out tree and crashes into
  // the ErrorBoundary "Oops!" screen).
  const loggingOutRef = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const activeUser = activeUserRef.current;
      if (!activeUser) return;

      if (event === 'SIGNED_OUT' && !session) {
        // Intentional logout tears down via full page reload — don't also flip
        // React state here (that re-render is what crashed into the ErrorBoundary).
        if (loggingOutRef.current) return;
        console.log('🔒 Session ended elsewhere, returning to login screen');
        setUser(null);
        setFamily(null);
        setIsAuthenticated(false);
        return;
      }

      if (session?.user && session.user.id !== activeUser.id) {
        console.warn('⚠️ Auth session switched accounts:', {
          thisTab: activeUser.email,
          nowSignedInAs: session.user.email
        });
        toast.error(`This browser is now signed in as ${session.user.email}. Reloading...`);
        setTimeout(() => window.location.reload(), 2000);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthSuccess = (authData) => {
    setUser(authData.user);
    setFamily(authData.family);
    setIsAuthenticated(true);
    setIsLoading(false); // 🔥 CRITICAL FIX: Stop loading state after auth
  };

  const handleLogout = async () => {
    // Robust logout: clear the session, then do a FULL page load to the auth
    // screen. We intentionally do NOT flip React auth state (setIsAuthenticated
    // /setUser/setFamily) — re-rendering the running app into a half-logged-out
    // tree is what threw into ErrorBoundary ("Oops! Something went wrong"). A
    // hard navigation tears everything down cleanly instead.
    loggingOutRef.current = true;

    let navigated = false;
    const finish = () => {
      if (navigated) return;
      navigated = true;
      window.location.assign('/');
    };

    // Never strand the user on a frozen screen if network sign-out is slow.
    const guard = setTimeout(finish, 2500);

    // Service teardown is best-effort; the page unload finishes anything pending.
    try {
      const { default: WebRTCService } = await import('../services/WebRTCService');
      await WebRTCService.shutdown();
    } catch (e) {
      console.warn('WebRTC shutdown during logout failed:', e?.message);
    }

    try {
      await ApiService.logout(); // clears the local auth session
    } catch (e) {
      console.warn('Logout error (session cleared locally):', e?.message);
    }

    clearTimeout(guard);
    finish();
  };

  // Complete task and update data
  const completeTask = async (taskId, kidId, completionData = {}) => {
    try {
      console.log('🔄 Starting task completion:', { taskId, kidId, completionData });
      
      const kid = family?.kids?.find(k => k.id === kidId);
      if (!kid) {
        console.error('❌ Kid not found:', kidId);
        toast.error('Kid not found');
        return false;
      }

      // Find task in the kid's tasks array
      const task = kid.tasks?.find(t => t.id === taskId);
      if (!task) {
        console.error('❌ Task not found:', taskId, 'in kid tasks:', kid.tasks);
        toast.error('Task not found');
        return false;
      }

      console.log('📋 Task found:', task);

      // Use the proper Supabase helper function for task completion
      const result = await ApiService.completeTask(kidId, taskId, completionData);
      
      console.log('✅ Task completion result:', result);

      // Refresh family data to get updated tasks and points
      const refreshedFamily = await ApiService.getFamilyData();
      setFamily(refreshedFamily);

      // Show success notification
      const pointsAwarded = task.points || 0;
      toast.success(`🎉 Great job! ${kid.name} earned ${pointsAwarded} points!`);
      
      NotificationService.sendNotification(
        '🏆 Task Completed!',
        `${kid.name} earned ${pointsAwarded} points for completing "${task.title}"`
      );

      console.log('✅ Task completion successful!');
      return true;
    } catch (error) {
      console.error('❌ Error completing task:', error);
      toast.error('Failed to complete task: ' + error.message);
      return false;
    }
  };

  // Kids management
  const addNewKid = async (kidData) => {
    try {
      if (!isOnline) {
        toast.error('Adding kids requires internet connection');
        return;
      }

      console.log('🔄 Adding kid:', kidData);
      console.log('📊 Current family state before adding:', family);
      console.log('👥 Current kids count:', family?.kids?.length || 0);
      
      const result = await ApiService.addKid(kidData);
      
      console.log('✅ Kid added successfully:', result);
      console.log('📊 Updated family data:', result.family);
      console.log('👥 New kids count:', result.family?.kids?.length || 0);
      
      // Force update the family state
      setFamily(result.family);
      
      // Also update localStorage to ensure consistency
      localStorage.setItem('familyData', JSON.stringify(result.family));
      
      // Add a small delay and then verify the state update
      setTimeout(() => {
        console.log('🔄 Family state updated, verifying...');
        logDebugInfo();
        
        // Force a re-render by getting fresh data if the kid still doesn't appear
        const currentKids = JSON.parse(localStorage.getItem('familyData') || '{}').kids || [];
        const kidExists = currentKids.some(k => k.id === result.kid.id);
        
        if (!kidExists) {
          console.log('⚠️ Kid not found in localStorage, forcing refresh...');
          forceRefreshData();
        } else {
          console.log('✅ Kid successfully found in family data');
        }
      }, 100);
      
      toast.success(`${kidData.name} added to the family! 👨‍👩‍👧‍👦`);
      return result.kid;
    } catch (error) {
      console.error('❌ Failed to add kid:', error);
      
      if (error.message) {
        toast.error(`Failed to add ${kidData.name}: ${error.message}`);
      } else {
        toast.error(`Failed to add ${kidData.name}. Please try again.`);
      }
      throw error;
    }
  };

  const removeKid = async (kidId) => {
    try {
      if (!isOnline) {
        toast.error('Removing kids requires internet connection');
        return;
      }

      const kid = family?.kids?.find(k => k.id === kidId);
      const updatedFamily = await ApiService.removeKid(kidId);
      setFamily(updatedFamily);
      toast.success(`${kid?.name} removed from family`);
    } catch (error) {
      // Remove kid failed silently logged
      toast.error(error.message || 'Failed to remove kid');
      throw error;
    }
  };

  const updateKidProfile = async (kidId, updates) => {
    try {
      if (!isOnline) {
        ApiService.queueOfflineChange({
          type: 'updateKid',
          kidId,
          updates
        });
        
        // Update local state optimistically
        setFamily(prev => ({
          ...prev,
          kids: prev?.kids?.map(kid => 
            kid.id === kidId ? { ...kid, ...updates } : kid
          ) || []
        }));
        
        toast.success('Profile updated! Will sync when online.');
        return;
      }

      const updatedFamily = await ApiService.updateKid(kidId, updates);
      setFamily(updatedFamily);
      toast.success('Profile updated!');
    } catch (error) {
      // Update kid failed silently logged
      toast.error(error.message || 'Failed to update profile');
      throw error;
    }
  };

  const updateKidPhoto = async (kidId, photoData, faceDescriptor = null) => {
    try {
      const updates = { profile_photo: photoData }
      if (faceDescriptor) {
        updates.face_descriptor = Array.isArray(faceDescriptor)
          ? faceDescriptor
          : Array.from(faceDescriptor)
      }
      await updateKidProfile(kidId, updates)
    } catch (error) {
      const errorMessage = error?.message || 'Failed to update photo'
      toast.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const setParentPin = async (pin) => {
    try {
      if (!isOnline) {
        toast.error('Setting a parent PIN requires internet connection')
        return
      }
      const updatedFamily = await ApiService.setParentPin(pin)
      setFamily(updatedFamily)
      toast.success(pin ? 'Parent PIN saved' : 'Parent PIN cleared')
      return updatedFamily
    } catch (error) {
      toast.error(error.message || 'Failed to save parent PIN')
      throw error
    }
  }

  const updateKidPreferences = async (kidId, preferences) => {
    try {
      const updates = { preferences };
      await updateKidProfile(kidId, updates);
    } catch (error) {
      // Update kid preferences failed silently logged
      toast.error(error.message || 'Failed to update preferences');
      throw error;
    }
  };

  // Shopping List management
  const addShoppingItem = async (itemData) => {
    try {
      if (!isOnline) {
        ApiService.queueOfflineChange({
          type: 'addShoppingItem',
          data: itemData
        });
        
        // Update local state optimistically
        const newItem = {
          id: `temp-${Date.now()}`,
          ...itemData,
          purchased: false
        };
        
        setFamily(prev => ({
          ...prev,
          shoppingList: [...(prev?.shoppingList || []), newItem]
        }));
        
        toast.success('Item added! Will sync when online.');
        return newItem;
      }

      const result = await ApiService.addShoppingItem(itemData);
      setFamily(result.family);
      return result.item;
    } catch (error) {
      // Add shopping item failed silently logged
      toast.error(error.message || 'Failed to add item');
      throw error;
    }
  };

  const removeShoppingItem = async (itemId) => {
    try {
      if (!isOnline) {
        ApiService.queueOfflineChange({
          type: 'removeShoppingItem',
          itemId
        });
        
        // Update local state optimistically
        setFamily(prev => ({
          ...prev,
          shoppingList: prev?.shoppingList?.filter(item => item.id !== itemId) || []
        }));
        
        toast.success('Item removed! Will sync when online.');
        return;
      }

      const result = await ApiService.removeShoppingItem(itemId);
      setFamily(result.family);
    } catch (error) {
      // Remove shopping item failed silently logged
      toast.error(error.message || 'Failed to remove item');
      throw error;
    }
  };

  const toggleShoppingItem = async (itemId) => {
    try {
      if (!isOnline) {
        ApiService.queueOfflineChange({
          type: 'toggleShoppingItem',
          itemId
        });
        
        // Update local state optimistically
        setFamily(prev => ({
          ...prev,
          shoppingList: prev?.shoppingList?.map(item => 
            item.id === itemId ? { ...item, purchased: !item.purchased } : item
          ) || []
        }));
        
        toast.success('Item updated! Will sync when online.');
        return;
      }

      const result = await ApiService.toggleShoppingItem(itemId);
      setFamily(result.family);
    } catch (error) {
      // Toggle shopping item failed silently logged
      toast.error(error.message || 'Failed to update item');
      throw error;
    }
  };

  // Task management
  const addTaskToKid = async (kidId, taskData) => {
    try {
      console.log('Adding task:', taskData, 'to kid:', kidId);
      const result = await ApiService.addTaskToKid(kidId, taskData);
      console.log('API result:', result);
      console.log('Updated family:', result.family);
      
      setFamily(result.family);
      toast.success('Task added successfully! 📝');
      return result.task;
    } catch (error) {
      console.error('Add task failed:', error);
      toast.error(error.message || 'Failed to add task');
      throw error;
    }
  };

  const removeTaskFromKid = async (kidId, taskId) => {
    try {
      const result = await ApiService.removeTaskFromKid(kidId, taskId);
      setFamily(result.family);
      toast.success('Task removed successfully! 🗑️');
    } catch (error) {
      // Remove task failed silently logged
      toast.error(error.message || 'Failed to remove task');
      throw error;
    }
  };

  // Family Challenges management
  const createFamilyChallenge = async (challengeData) => {
    try {
      if (!isOnline) {
        // Update local state optimistically for offline
        const newChallenge = {
          id: `temp-${Date.now()}`,
          ...challengeData,
          createdAt: new Date().toISOString(),
          active: true
        };
        
        setFamily(prev => ({
          ...prev,
          familyChallenges: [...(prev?.familyChallenges || []), newChallenge]
        }));
        
        toast.success('Challenge created! Will sync when online.');
        return newChallenge;
      }

      // For now, just add to local state as we don't have Supabase implementation yet
      const newChallenge = {
        id: `challenge-${Date.now()}`,
        ...challengeData,
        createdAt: new Date().toISOString(),
        active: true
      };
      
      setFamily(prev => ({
        ...prev,
        familyChallenges: [...(prev?.familyChallenges || []), newChallenge]
      }));

      // Also update localStorage
      const currentFamily = JSON.parse(localStorage.getItem('familyData') || '{}');
      currentFamily.familyChallenges = [...(currentFamily.familyChallenges || []), newChallenge];
      localStorage.setItem('familyData', JSON.stringify(currentFamily));
      
      toast.success('Family challenge created successfully! 🏆');
      return newChallenge;
    } catch (error) {
      // Create family challenge failed silently logged
      toast.error(error.message || 'Failed to create challenge');
      throw error;
    }
  };

  // Getters
  const getLeaderboard = () => {
    if (!family?.kids) return [];
    
    return [...family.kids]
      .sort((a, b) => b.points - a.points)
      .map((kid, index) => ({
        ...kid,
        rank: index + 1,
        badge: index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅'
      }));
  };

  const getKidStats = (kidId) => {
    const kid = family?.kids?.find(k => k.id === kidId);
    if (!kid) return null;

    return {
      ...kid,
      totalPoints: kid.points,
      tasksThisWeek: kid.stats?.weeklyProgress?.reduce((sum, day) => sum + day, 0) || 0,
      averageTasksPerDay: kid.stats?.totalTasksCompleted ? 
        (kid.stats.totalTasksCompleted / Math.max(1, Math.floor((Date.now() - new Date(kid.createdAt)) / (1000 * 60 * 60 * 24)))) : 0,
      favoriteCategory: kid.stats?.favoriteTaskCategory || 'none'
    };
  };

  const getParentDashboardData = () => {
    if (!family) return null;

    const totalPoints = family.kids?.reduce((sum, kid) => sum + kid.points, 0) || 0;
    const totalTasks = family.kids?.reduce((sum, kid) => sum + kid.completedTasks, 0) || 0;
    const activeChallenges = family.familyChallenges?.filter(c => c.isActive)?.length || 0;
    const unreadMessages = family.messages?.filter(m => !m.read)?.length || 0;

    return {
      totalKids: family.kids?.length || 0,
      totalPoints,
      totalTasks,
      activeChallenges,
      unreadMessages,
      recentCompletions: family.completions?.slice(-10) || [],
      topPerformer: getLeaderboard()[0] || null
    };
  };

  // Utility function to check if a task should appear today
  const shouldTaskAppearToday = (task) => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Non-recurring tasks always appear (until completed)
    if (!task.recurring || task.recurring_pattern === 'once') {
      return true;
    }
    
    switch (task.recurring_pattern) {
      case 'daily':
        return true;
      
      case 'weekdays':
        return dayOfWeek >= 1 && dayOfWeek <= 5; // Monday(1) to Friday(5)
      
      case 'weekends':
        return dayOfWeek === 0 || dayOfWeek === 6; // Sunday(0) or Saturday(6)
      
      case 'custom':
        if (task.recurring_data?.customDays) {
          return task.recurring_data.customDays.includes(dayOfWeek);
        }
        return true; // Fallback to show if no custom days specified
      
      default:
        return true; // Fallback to show unknown patterns
    }
  };

  // Create dailyTasks organized by kidId for KidTasks component
  const getDailyTasks = () => {
    const dailyTasksObj = {};
    family?.kids?.forEach(kid => {
      // Filter current tasks by schedule
      const currentTasks = (kid.tasks || []).filter(task => {
        // Completed tasks don't need schedule filtering (they're done)
        if (task.completed) return true;
        
        // Apply schedule filtering for active tasks
        return shouldTaskAppearToday(task);
      });
      
      const historicalTasks = kid.completedTasksHistory || [];
      
      // Show filtered current tasks first, then historical completions
      dailyTasksObj[kid.id] = [...currentTasks, ...historicalTasks];
    });
    return dailyTasksObj;
  };

  const refreshFamilyData = async () => {
    try {
      if (!isOnline) {
        toast.error('Need internet connection to refresh data');
        return;
      }
      
      const freshFamily = await ApiService.getFamilyData();
      setFamily(freshFamily);
      toast.success('Data refreshed! 🔄');
    } catch (error) {
      console.error('Failed to refresh family data:', error);
      toast.error('Failed to refresh data');
    }
  };

  // Add this debugging function at the top level
  const logDebugInfo = () => {
    console.log('=== FAMILY DATA DEBUG ===');
    console.log('Family object:', family);
    console.log('Kids array:', family?.kids);
    console.log('Kids count:', family?.kids?.length || 0);
    console.log('localStorage family:', JSON.parse(localStorage.getItem('familyData') || '{}'));
    console.log('localStorage user:', JSON.parse(localStorage.getItem('user') || '{}'));
    console.log('=========================');
  };

  // Add a force refresh function
  const forceRefreshData = async () => {
    try {
      console.log('🔄 Force refreshing family data...');
      
      const freshFamily = await ApiService.getFamilyData();
      console.log('📊 Fresh family data:', freshFamily);
      
      setFamily(freshFamily);
      localStorage.setItem('familyData', JSON.stringify(freshFamily));
      
      logDebugInfo();
      toast.success('Data refreshed! 🔄');
    } catch (error) {
      console.error('❌ Failed to refresh:', error);
      toast.error('Failed to refresh data');
    }
  };

  const value = {
    // State
    family,
    kids: family?.kids || [],
    tasks: family?.tasks || [],
    dailyTasks: getDailyTasks(), // Add this for KidTasks component
    shoppingList: family?.shoppingList || [],
    familyChallenges: family?.familyChallenges || [],
    messages: family?.messages || [],
    isOnline,
    user,
    isAuthenticated,
    
    // Actions
    handleAuthSuccess,
    handleLogout,
    completeTask,
    addNewKid,
    removeKid,
    updateKidProfile,
    updateKidPhoto,
    setParentPin,
    updateKidPreferences,
    addShoppingItem,
    removeShoppingItem,
    toggleShoppingItem,
    createFamilyChallenge,
    addTaskToKid,
    removeTaskFromKid,
    forceRefreshData, // Add this
    logDebugInfo,     // Add this
    refreshFamilyData, // Add this line
    
    // Utility functions
    getLeaderboard,
    getKidStats,
    getParentDashboardData,
    
    // API Service for debugging
    supabase: ApiService,

    // App Settings
    appSettings,
    updateAppSettings,

    // Push Notifications
    pushNotificationState,
    requestNotificationPermission,
    unsubscribeFromPush,
    sendLocalNotification,

    // Presence & Calling (moved from above to avoid duplicates)
    onlineMembers,
    isUserOnline,
    isAvailableForCalls,
    toggleAvailability,
    refreshPresence
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export { DataContext };
export default DataProvider; 